import { useCallback, useEffect, useMemo } from 'react';
import { useURLSearchParams } from '../../../src/utils/hooks/useURLSearchParams';
import { decodeUrlParam, encodeUrlParam } from './base64-utf8';

export interface Options<T> {
	queryName: string;
	type?: 'string' | 'number' | 'boolean' | 'object';
	defaultValue?: T;
}

/**
 * Reads a raw query string back into a value.
 *
 * The overload is the one unchecked seam in this file: `type` decides the runtime shape while the
 * caller of `useStateQuery` picks T separately, and nothing can prove the two agree. Keeping that
 * trust here means no `as` at the point of use — but it is still trust, not a guarantee. Only
 * overloading `useStateQuery` itself would make it checkable, at the cost of every call site.
 */
function readQueryValue<T>(raw: string | undefined, type: Options<unknown>['type']): T;
function readQueryValue(raw: string | undefined, type: Options<unknown>['type']): unknown {
	if (type === 'boolean') return raw !== undefined;
	if (type === 'string' || type === undefined) return raw ?? '';
	if (raw === undefined) return undefined;
	if (type === 'number') return parseInt(raw, 10);
	if (type === 'object') return decodeUrlParam(raw);
	return raw;
}

export function useStateQuery<T>({
	queryName,
	type,
	defaultValue,
}: Options<T>): [T, (value: T | null) => void, () => void] {
	const { searchParams, setQuery, deleteQuery } = useURLSearchParams();
	const setValue = useCallback(
		(newValue: T | null) => {
			if (newValue === null) {
				deleteQuery(queryName);
				return;
			}
			if (type === 'boolean') {
				if (newValue) setQuery(queryName);
				else deleteQuery(queryName);
			}
			if (type === 'string' || type === undefined) {
				if (newValue !== '') setQuery(queryName, String(newValue));
				else deleteQuery(queryName);
			}
			if (type === 'number') setQuery(queryName, newValue === undefined ? undefined : String(newValue));
			if (type === 'object') {
				if (newValue) setQuery(queryName, encodeUrlParam(newValue));
				else deleteQuery(queryName);
			}
		},
		[type, setQuery, queryName, deleteQuery],
	);

	const clearQuery = useCallback(() => {
		deleteQuery(queryName);
	}, [deleteQuery, queryName]);

	const value = useMemo<T>(() => {
		try {
			return readQueryValue<T>(searchParams[queryName], type);
		} catch (_) {
			/* Malformed param — drop it and read it as absent rather than surfacing a decode error. */
			deleteQuery(queryName);
			return readQueryValue<T>(undefined, type);
		}
	}, [deleteQuery, queryName, searchParams, type]);

	useEffect(() => {
		const v = searchParams[queryName];
		if (!v && defaultValue) {
			setValue(defaultValue);
		}
	}, [defaultValue, queryName, searchParams, setValue]);

	useEffect(() => {
		const mountPathname = window.location.pathname;
		return () => {
			if (window.location.pathname !== mountPathname) {
				deleteQuery(queryName);
			}
		};
	}, [deleteQuery, queryName]);

	return [value, setValue, clearQuery];
}
